import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { ReactQRCode } from '@lglab/react-qr-code';
import { InvoiceImageSize } from '../invoiceImages';

const QR_SIZE = 480;
const IMAGE_WIDTH = 200;
const IMAGE_HEIGHT = 80;
const CANVAS_WIDTH = 1000;

export interface StickerQrImage {
    dataUrl: string;
    width: number;
    height: number;
}

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not render sticker QR code'));
    image.src = src;
});

const logoBox = (svg: SVGSVGElement) => {
    const image = svg.querySelector('image');
    const viewWidth = svg.viewBox.baseVal?.width || 0;
    if (!image || viewWidth <= 0) return null;
    let x = Number(image.getAttribute('x')) || 0;
    let y = Number(image.getAttribute('y')) || 0;
    const width = Number(image.getAttribute('width')) || 0;
    const height = Number(image.getAttribute('height')) || 0;
    if (width <= 0 || height <= 0) return null;
    let node: Element | null = image.parentElement;
    while (node && node !== svg) {
        const transform = node.getAttribute('transform') || '';
        const match = transform.match(/translate\(\s*(-?\d*\.?\d+)[\s,]+(-?\d*\.?\d+)\s*\)/);
        if (match) {
            x += Number(match[1]);
            y += Number(match[2]);
        }
        node = node.parentElement;
    }
    const scale = CANVAS_WIDTH / viewWidth;
    return {
        x: x * scale,
        y: y * scale,
        width: width * scale,
        height: height * scale,
    };
};

const drawLogo = (
    context: CanvasRenderingContext2D,
    logo: HTMLImageElement,
    box: { x: number; y: number; width: number; height: number },
    logoSize?: InvoiceImageSize,
) => {
    const innerWidth = box.width;
    const innerHeight = box.height;
    const naturalWidth = logo.naturalWidth || logoSize?.width || innerWidth;
    const naturalHeight = logo.naturalHeight || logoSize?.height || innerHeight;
    if (naturalWidth <= 0 || naturalHeight <= 0 || innerWidth <= 0 || innerHeight <= 0) return;
    const fit = Math.min(innerWidth / naturalWidth, innerHeight / naturalHeight);
    const drawWidth = naturalWidth * fit;
    const drawHeight = naturalHeight * fit;
    context.drawImage(
        logo,
        box.x + (box.width - drawWidth) / 2,
        box.y + (box.height - drawHeight) / 2,
        drawWidth,
        drawHeight,
    );
};

const viewBoxSize = (svg: SVGSVGElement) => {
    const parts = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    const width = parts[2];
    const height = parts[3];
    if (width > 0 && height > 0) return { width, height };
    const box = svg.viewBox?.baseVal;
    if (box?.width > 0 && box?.height > 0) return { width: box.width, height: box.height };
    return { width: QR_SIZE, height: QR_SIZE };
};

const rasterizeQrSvg = async (
    svg: SVGSVGElement,
    logoUrl: string,
    logoSize?: InvoiceImageSize,
): Promise<StickerQrImage> => {
    const box = logoBox(svg);
    const viewBox = viewBoxSize(svg);
    const viewWidth = viewBox.width;
    const viewHeight = viewBox.height;
    const height = Math.max(1, Math.round(CANVAS_WIDTH * (viewHeight / viewWidth)));
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll('image').forEach((node) => node.remove());
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(CANVAS_WIDTH));
    clone.setAttribute('height', String(height));
    const xml = new XMLSerializer().serializeToString(clone);
    const svgUrl = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
    try {
        const image = await loadImage(svgUrl);
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_WIDTH;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Could not render sticker QR code');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, CANVAS_WIDTH, height);
        context.drawImage(image, 0, 0, CANVAS_WIDTH, height);
        if (logoUrl && box) {
            try {
                const logo = await loadImage(logoUrl);
                drawLogo(context, logo, box, logoSize);
            } catch {
                // Keep the excavated QR when the center logo cannot be drawn.
            }
        }
        return {
            dataUrl: canvas.toDataURL('image/png'),
            width: CANVAS_WIDTH,
            height,
        };
    } finally {
        URL.revokeObjectURL(svgUrl);
    }
};

export const renderStickerQrDataUrl = async (
    value: string,
    logoUrl = '',
    logoSize?: InvoiceImageSize,
): Promise<StickerQrImage> => {
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.position = 'fixed';
    host.style.left = '-10000px';
    host.style.top = '0';
    document.body.appendChild(host);
    const root = createRoot(host);
    try {
        flushSync(() => {
            root.render(createElement(ReactQRCode, {
                value,
                size: QR_SIZE,
                level: 'Q',
                marginSize: 5,
                background: '#ffffff',
                dataModulesSettings: { style: 'circle' },
                finderPatternOuterSettings: {
                    style: 'inpoint-lg',
                    color: '#ff0000',
                },
                finderPatternInnerSettings: { style: 'rounded-lg' },
                frameSettings: {
                    style: 'ticket',
                    text: '¡Escanéame!',
                    color: '#000000',
                    textColor: '#ffffff',
                    fontFamily: 'Arial, Helvetica, sans-serif',
                },
                imageSettings: logoUrl ? {
                    src: logoUrl,
                    width: IMAGE_WIDTH,
                    height: IMAGE_HEIGHT,
                    excavate: true,
                } : undefined,
            }));
        });
        const svg = host.querySelector('svg');
        if (!svg) throw new Error('Could not render sticker QR code');
        return await rasterizeQrSvg(svg, logoUrl, logoSize);
    } finally {
        root.unmount();
        host.remove();
    }
};
